/**
 * Spinner wheel.
 *
 * Three bits of logic matter, and all are pure so they can be tested without a canvas:
 *
 *  - `winningSlice` — which slice sits under the pointer once the wheel stops. Canvas
 *    angles start at 3 o'clock and the pointer is at 12, so the quarter turn between them
 *    has to be accounted for, or the wheel announces a winner that is not the one it is
 *    visibly pointing at.
 *  - `rebalance` — slices are sized by percentage, so changing one has to move the others.
 *  - `buildSteps` / `insertBonus` — a game is a list of steps, and landing on certain
 *    options splices an extra step in immediately after. Bonus rounds have their own
 *    wheel, and are simply never reached when nothing triggers them.
 */

export interface Segment {
  id: string
  label: string
  colour: string
  /** Share of its wheel, in percent. Each wheel's set sums to 100. */
  weight: number
  /** Landing here plays this bonus round next. Null for most options. */
  bonusRoundId: string | null
  /**
   * A percentage you typed yourself. Locked slices are left alone when another slice is
   * adjusted — otherwise setting one to 40 and the next to 30 would quietly move the
   * first one again.
   */
  locked?: boolean
}

export interface RoundConfig {
  id: string
  title: string
  /** Each round has its own wheel — rounds no longer share one set of options. */
  segments: Segment[]
}

/** An extra round with its own options, played only when something triggers it. */
export interface BonusRound {
  id: string
  title: string
  segments: Segment[]
}

export interface GameConfig {
  rounds: RoundConfig[]
  bonusRounds: BonusRound[]
}

/** One round to play: a title and the wheel it uses. */
export interface Step {
  key: string
  title: string
  segments: Segment[]
  bonus: boolean
}

export interface RoundResult {
  title: string
  label: string
  colour: string
  bonus: boolean
  /** The bonus round this result triggered, if any. */
  triggered: string | null
}

export const PALETTE = [
  '#10b981',
  '#6366f1',
  '#f59e0b',
  '#f43f5e',
  '#0ea5e9',
  '#8b5cf6',
  '#ec4899',
  '#84cc16',
]

export const MIN_SEGMENTS = 2
export const MAX_SEGMENTS = 8
export const MIN_WEIGHT = 1
export const MIN_ROUNDS = 1
export const MAX_ROUNDS = 20
export const MAX_BONUS_ROUNDS = 6

/** 30% quicker than the original 4000 ms. */
export const SPIN_DURATION_MS = Math.round(4000 / 1.3)

const MIN_FULL_ROTATIONS = 5
const MAX_FULL_ROTATIONS = 8

export const TAU = Math.PI * 2

export function defaultSegments(prefix: string): Segment[] {
  return [
    { id: `${prefix}-1`, label: 'Option 1', colour: PALETTE[0], weight: 25, bonusRoundId: null },
    { id: `${prefix}-2`, label: 'Option 2', colour: PALETTE[1], weight: 25, bonusRoundId: null },
    { id: `${prefix}-3`, label: 'Option 3', colour: PALETTE[2], weight: 25, bonusRoundId: null },
    { id: `${prefix}-4`, label: 'Option 4', colour: PALETTE[3], weight: 25, bonusRoundId: null },
  ]
}

export function emptyGame(): GameConfig {
  return {
    rounds: [
      { id: 'round-1', title: 'Round 1', segments: defaultSegments('r1') },
      { id: 'round-2', title: 'Round 2', segments: defaultSegments('r2') },
      { id: 'round-3', title: 'Round 3', segments: defaultSegments('r3') },
    ],
    bonusRounds: [],
  }
}

/** Copies a wheel for a new round, with fresh ids so the two cannot be confused. */
export function cloneSegments(segments: Segment[], prefix: string): Segment[] {
  return segments.map((s, i) => ({ ...s, id: `${prefix}-${i}` }))
}

export function emptyBonusRound(id: string): BonusRound {
  return {
    id,
    title: 'Bonus round',
    segments: [
      { id: `${id}-a`, label: 'Option A', colour: PALETTE[4], weight: 50, bonusRoundId: null },
      { id: `${id}-b`, label: 'Option B', colour: PALETTE[5], weight: 50, bonusRoundId: null },
    ],
  }
}

// ------------------------------------------------------------------ geometry

/** Start and end angle of each slice, before rotation. Sized by weight. */
export function sliceRanges(segments: Segment[]): { start: number; end: number }[] {
  const total = segments.reduce((sum, s) => sum + s.weight, 0) || 1
  const ranges: { start: number; end: number }[] = []
  let angle = 0
  for (const segment of segments) {
    ranges.push({ start: angle, end: angle + (segment.weight / total) * TAU })
    angle = ranges[ranges.length - 1].end
  }
  // Absorb floating-point drift into the last slice so the wheel always closes.
  if (ranges.length > 0) ranges[ranges.length - 1].end = TAU
  return ranges
}

/**
 * Which slice is under the pointer at 12 o'clock.
 *
 * Slice i covers [start + rotation, end + rotation) in canvas space, where angle 0 points
 * right. The pointer sits at -π/2, hence the subtraction.
 */
export function winningSlice(rotation: number, segments: Segment[]): number {
  if (segments.length === 0) return 0
  const pointer = -Math.PI / 2
  const normalised = (((pointer - rotation) % TAU) + TAU) % TAU
  const ranges = sliceRanges(segments)
  for (let i = 0; i < ranges.length; i++) {
    if (normalised >= ranges[i].start && normalised < ranges[i].end) return i
  }
  return ranges.length - 1
}

export function spinTarget(rotation: number, random: () => number = Math.random): number {
  const turns =
    Math.floor(random() * (MAX_FULL_ROTATIONS - MIN_FULL_ROTATIONS + 1)) + MIN_FULL_ROTATIONS
  return rotation + turns * TAU + random() * TAU
}

export function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

// -------------------------------------------------------------------- steps

/** The rounds a game starts with: the configured ones, all using the main wheel. */
export function buildSteps(config: GameConfig): Step[] {
  return config.rounds.map((round, i) => ({
    key: round.id,
    title: round.title.trim() || `Round ${i + 1}`,
    segments: round.segments,
    bonus: false,
  }))
}

/**
 * Splices a bonus round in directly after the round that triggered it, so it reads as a
 * consequence rather than something bolted onto the end. Returns the list unchanged when
 * the bonus round no longer exists — configuration can change between games.
 */
export function insertBonus(
  steps: Step[],
  afterIndex: number,
  config: GameConfig,
  bonusRoundId: string,
  keySuffix: string
): Step[] {
  const bonus = config.bonusRounds.find((b) => b.id === bonusRoundId)
  if (!bonus || bonus.segments.length < MIN_SEGMENTS) return steps
  const step: Step = {
    key: `${bonus.id}-${keySuffix}`,
    title: bonus.title.trim() || 'Bonus round',
    segments: bonus.segments,
    bonus: true,
  }
  return [...steps.slice(0, afterIndex + 1), step, ...steps.slice(afterIndex + 1)]
}

// ------------------------------------------------------------------ weights

export function nextColour(existing: Segment[]): string {
  const used = new Set(existing.map((s) => s.colour))
  return PALETTE.find((c) => !used.has(c)) ?? PALETTE[existing.length % PALETTE.length]
}

/**
 * Sets one slice's percentage and absorbs the difference into the others, in proportion to
 * their current sizes — so nudging one option does not silently reshuffle the ranking of
 * the rest. Every slice keeps at least [MIN_WEIGHT]%, and the set always sums to 100.
 */
export function rebalance(segments: Segment[], id: string, requested: number): Segment[] {
  if (segments.length < 2) return segments

  const others = segments.filter((s) => s.id !== id)
  // Locked slices hold their value; only the rest absorb the difference. If everything
  // else is locked there is nowhere to put the change, so the edit is refused rather than
  // silently breaking someone's carefully set numbers.
  const movable = others.filter((s) => !s.locked)
  if (movable.length === 0) return segments

  const lockedTotal = others.filter((s) => s.locked).reduce((sum, s) => sum + s.weight, 0)
  const maxForOne = 100 - lockedTotal - movable.length * MIN_WEIGHT
  if (maxForOne < MIN_WEIGHT) return segments

  const target = clamp(Math.round(requested), MIN_WEIGHT, maxForOne)
  const remaining = 100 - target - lockedTotal
  const movableTotal = movable.reduce((sum, s) => sum + s.weight, 0)
  const shares = movable.map((s) =>
    movableTotal > 0 ? (s.weight / movableTotal) * remaining : remaining / movable.length
  )

  const floored = shares.map((v) => Math.max(MIN_WEIGHT, Math.floor(v)))
  let slack = remaining - floored.reduce((sum, v) => sum + v, 0)

  const order = floored.map((value, index) => ({ index, value })).sort((a, b) => b.value - a.value)
  let cursor = 0
  while (slack > 0 && order.length > 0) {
    floored[order[cursor % order.length].index] += 1
    slack -= 1
    cursor += 1
  }
  while (slack < 0 && order.length > 0) {
    const candidate = order[cursor % order.length].index
    if (floored[candidate] > MIN_WEIGHT) {
      floored[candidate] -= 1
      slack += 1
    }
    cursor += 1
    if (cursor > order.length * 200) break
  }

  const byId = new Map(movable.map((s, i) => [s.id, floored[i]]))
  return segments.map((s) => {
    if (s.id === id) return { ...s, weight: target, locked: true }
    const next = byId.get(s.id)
    return next === undefined ? s : { ...s, weight: next }
  })
}

/** Frees a slice to move again when its neighbours are adjusted. */
export function unlockSegment(segments: Segment[], id: string): Segment[] {
  return segments.map((s) => (s.id === id ? { ...s, locked: false } : s))
}

/** Used after adding or removing a slice, where there is no single edited value. */
export function evenWeights(segments: Segment[]): Segment[] {
  if (segments.length === 0) return segments
  const base = Math.floor(100 / segments.length)
  let slack = 100 - base * segments.length
  // Adding or removing a slice resets the locks too: the old numbers no longer add up,
  // so keeping them pinned would only produce an unsolvable set.
  return segments.map((s) => {
    const extra = slack > 0 ? 1 : 0
    slack -= extra
    return { ...s, weight: base + extra, locked: false }
  })
}

export function weightsAreValid(segments: Segment[]): boolean {
  const total = segments.reduce((sum, s) => sum + s.weight, 0)
  return total === 100 && segments.every((s) => s.weight >= MIN_WEIGHT)
}

/** Free-text percentage entry: digits only, empty means "leave it alone". */
export function parsePercent(raw: string): number | null {
  const trimmed = raw.trim()
  if (trimmed === '' || !/^\d{1,3}$/.test(trimmed)) return null
  const value = Number(trimmed)
  return value >= 1 && value <= 99 ? value : null
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}
